import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Lock, ShieldCheck } from "./icons";
import { LanguageSelector } from "./LanguageSelector";

export type LoginMfaMode = "verify" | "setup" | "email";

type Props = {
  mode: LoginMfaMode;
  email: string;
  mfaToken: string;
  companyCode?: string;
  onSuccess: (token: string, email: string) => void;
  onBack: () => void;
};

async function postJson(url: string, body: Record<string, unknown>) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  return { ok: res.ok, status: res.status, data };
}

function compactAlnum(raw: string, max: number): string {
  return String(raw ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, max);
}

function looksLikeBackupCode(raw: string): boolean {
  const compact = compactAlnum(raw, 12);
  return /[A-Z]/.test(compact) || compact.length > 6;
}

const boxClass = (error?: boolean) =>
  `h-12 w-10 sm:h-14 sm:w-12 text-center text-xl font-semibold tracking-wide text-slate-900 bg-slate-50 border rounded-xl outline-none transition-all ${
    error ? "border-red-400 ring-2 ring-red-200" : "border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-400"
  } disabled:opacity-60`;

function scrollLoginFieldIntoView(target: HTMLElement) {
  const shell = target.closest(".rv-login-shell") as HTMLElement | null;
  if (!shell) return;
  const vvHeight = window.visualViewport?.height ?? window.innerHeight;
  const rect = target.getBoundingClientRect();
  const margin = 20;
  if (rect.top >= margin && rect.bottom <= vvHeight - margin) return;
  const delta = rect.top + rect.height / 2 - vvHeight * 0.36;
  shell.scrollTop = Math.max(0, shell.scrollTop + delta);
}

function OtpBoxes({
  value,
  onChange,
  disabled,
  error,
  autoSubmit,
  autoComplete,
  onBackupCode,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  error?: boolean;
  autoSubmit?: (complete: string) => void;
  autoComplete?: string;
  onBackupCode?: (compact: string) => void;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] ?? "");

  const focusAt = (i: number) => {
    const el = refs.current[Math.max(0, Math.min(5, i))];
    el?.focus();
    el?.select();
  };

  const keepRowVisible = () => {
    const row = rowRef.current;
    if (!row) return;
    const run = () => scrollLoginFieldIntoView(row);
    window.requestAnimationFrame(run);
    window.setTimeout(run, 80);
    window.setTimeout(run, 280);
  };

  const ingest = (raw: string, fromIndex = 0) => {
    if (onBackupCode && looksLikeBackupCode(raw)) {
      onBackupCode(compactAlnum(raw, 8));
      return;
    }
    const compact = raw.replace(/\D/g, "").slice(0, 6);
    onChange(compact);
    if (compact.length === 6) autoSubmit?.(compact);
    else if (fromIndex >= 0) focusAt(Math.min(5, compact.length));
  };

  return (
    <div
      ref={rowRef}
      className={`rv-otp-row flex justify-center gap-1.5 sm:gap-2 ${error ? "rv-otp-error" : ""}`}
    >
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          enterKeyHint="done"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          autoComplete={i === 0 ? autoComplete || "one-time-code" : "off"}
          maxLength={i === 0 ? (onBackupCode ? 8 : 6) : 1}
          disabled={disabled}
          aria-label={`Digit ${i + 1}`}
          value={d}
          onFocus={keepRowVisible}
          onChange={(e) => {
            const incoming = e.target.value;
            if (onBackupCode && looksLikeBackupCode(incoming)) {
              onBackupCode(compactAlnum(value.slice(0, i) + incoming, 8));
              return;
            }
            const raw = incoming.replace(/\D/g, "");
            if (raw.length > 1) {
              ingest((value.slice(0, i) + raw).slice(0, 6), i);
              return;
            }
            const next = (value.slice(0, i) + raw + value.slice(i + 1)).replace(/\D/g, "").slice(0, 6);
            onChange(next);
            if (raw) focusAt(i + 1);
            if (next.length === 6) autoSubmit?.(next);
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !digits[i] && i > 0) {
              e.preventDefault();
              onChange(value.slice(0, i - 1));
              focusAt(i - 1);
            } else if (e.key === "ArrowLeft") {
              e.preventDefault();
              focusAt(i - 1);
            } else if (e.key === "ArrowRight") {
              e.preventDefault();
              focusAt(i + 1);
            }
          }}
          onPaste={(e) => {
            e.preventDefault();
            ingest(value.slice(0, i) + e.clipboardData.getData("text"));
          }}
          className={`${boxClass(error)} tabular-nums`}
        />
      ))}
    </div>
  );
}

function BackupBoxes({
  value,
  onChange,
  disabled,
  error,
  autoSubmit,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  error?: boolean;
  autoSubmit?: (complete: string) => void;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const compact = compactAlnum(value, 8);
  const chars = Array.from({ length: 8 }, (_, i) => compact[i] ?? "");

  const focusAt = (i: number) => {
    const el = refs.current[Math.max(0, Math.min(7, i))];
    el?.focus();
    el?.select();
  };

  const keepRowVisible = () => {
    const row = rowRef.current;
    if (!row) return;
    const run = () => scrollLoginFieldIntoView(row);
    window.requestAnimationFrame(run);
    window.setTimeout(run, 80);
    window.setTimeout(run, 280);
  };

  const setCompact = (next: string, focusIndex?: number) => {
    const compactNext = compactAlnum(next, 8);
    onChange(compactNext);
    if (compactNext.length === 8) autoSubmit?.(compactNext);
    else if (typeof focusIndex === "number") focusAt(focusIndex);
  };

  useEffect(() => {
    const firstEmpty = Math.min(compact.length, 7);
    refs.current[firstEmpty]?.focus();
  }, []);

  return (
    <div ref={rowRef} className={`rv-otp-row flex justify-center items-center gap-1 sm:gap-1.5 ${error ? "rv-otp-error" : ""}`}>
      {chars.map((ch, i) => (
        <React.Fragment key={i}>
          {i === 4 && <span className="text-white/80 font-bold px-0.5 select-none">-</span>}
          <input
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="off"
            maxLength={i === 0 ? 8 : 1}
            disabled={disabled}
            aria-label={`Backup character ${i + 1}`}
            value={ch}
            onFocus={keepRowVisible}
            onChange={(e) => {
              const raw = compactAlnum(e.target.value, 8);
              if (raw.length > 1) {
                setCompact(compact.slice(0, i) + raw, Math.min(7, i + raw.length));
                return;
              }
              const next = compactAlnum(compact.slice(0, i) + raw + compact.slice(i + 1), 8);
              onChange(next);
              if (raw) focusAt(i + 1);
              if (next.length === 8) autoSubmit?.(next);
            }}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && !chars[i] && i > 0) {
                e.preventDefault();
                onChange(compact.slice(0, i - 1));
                focusAt(i - 1);
              } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                focusAt(i - 1);
              } else if (e.key === "ArrowRight") {
                e.preventDefault();
                focusAt(i + 1);
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              setCompact(compact.slice(0, i) + e.clipboardData.getData("text"), 7);
            }}
            className={boxClass(error)}
          />
        </React.Fragment>
      ))}
    </div>
  );
}

export const LoginMfaPanel: React.FC<Props> = ({ mode, email, mfaToken, companyCode, onSuccess, onBack }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<"code" | "setup" | "backup-ack">(mode === "setup" ? "setup" : "code");
  const [useBackup, setUseBackup] = useState(false);
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [qr, setQr] = useState<string>("");
  const [secret, setSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [token, setToken] = useState(mfaToken);
  const [resendWait, setResendWait] = useState(0);
  const [trustDevice, setTrustDevice] = useState(false);
  const trustDeviceRef = useRef(false);
  trustDeviceRef.current = trustDevice;
  const pendingToken = useRef<string | null>(null);
  const inFlight = useRef(false);
  const setupReq = useRef(0);

  const startSetup = useCallback(async () => {
    const reqId = ++setupReq.current;
    setError(null);
    setLoading(true);
    try {
      const { ok, status, data } = await postJson("/api/users/mfa/setup/start", { mfaToken });
      if (reqId !== setupReq.current) return;
      if (status === 409 || data.alreadyEnrolled) {
        setError(String(data.error || t("login.alreadyEnrolled")));
        return;
      }
      if (!ok) throw new Error(String(data.error || t("login.mfaSetupFailed")));
      setQr(String(data.qrDataUrl || ""));
      setSecret(String(data.secret || ""));
      setStep("setup");
    } catch (err: unknown) {
      if (reqId !== setupReq.current) return;
      setError(err instanceof Error ? err.message : t("login.mfaSetupFailed"));
    } finally {
      if (reqId === setupReq.current) setLoading(false);
    }
  }, [mfaToken, t]);

  const setupStartedFor = useRef<string | null>(null);
  useEffect(() => {
    if (mode !== "setup") return;
    if (setupStartedFor.current === mfaToken) return;
    setupStartedFor.current = mfaToken;
    void startSetup();
  }, [mode, mfaToken, startSetup]);

  const mfaBody = (extra: Record<string, unknown>) => ({
    ...extra,
    trustDevice: trustDeviceRef.current,
    ...(companyCode ? { companyCode } : {}),
  });

  const finishWithToken = (token: string) => {
    onSuccess(token, email);
  };

  const verifyLogin = async (otp: string) => {
    if (otp.length !== 6 || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const { ok, data } = await postJson("/api/users/mfa/verify", mfaBody({ mfaToken: token, code: otp }));
      if (!ok || !data.token) throw new Error(String(data.error || t("login.invalidCode")));
      finishWithToken(String(data.token));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("login.invalidCode"));
      setCode("");
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  const verifyBackup = async (otp?: string) => {
    const raw = compactAlnum(otp ?? backup, 8);
    if (raw.length !== 8 || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const { ok, data } = await postJson("/api/users/mfa/verify", mfaBody({ mfaToken: token, code: raw }));
      if (!ok || !data.token) throw new Error(String(data.error || t("login.invalidCode")));
      finishWithToken(String(data.token));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("login.invalidCode"));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  const startBackupEntry = (seed = "") => {
    const compact = compactAlnum(seed, 8);
    setUseBackup(true);
    setError(null);
    setCode("");
    setBackup(compact);
    if (compact.length === 8) void verifyBackup(compact);
  };

  const verifyEmail = async (otp: string) => {
    if (otp.length !== 6 || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const { ok, data } = await postJson("/api/users/mfa/email/verify", mfaBody({ mfaToken: token, code: otp }));
      if (!ok || !data.token) throw new Error(String(data.error || t("login.invalidEmailCode")));
      finishWithToken(String(data.token));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("login.invalidEmailCode"));
      setCode("");
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  const resendEmail = async () => {
    if (resendWait > 0 || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const { ok, data } = await postJson("/api/users/mfa/email/send", { mfaToken: token });
      if (!ok) throw new Error(String(data.error || t("login.resendFailed")));
      if (typeof data.mfaToken === "string" && data.mfaToken) setToken(data.mfaToken);
      setResendWait(45);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("login.resendFailed"));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    if (resendWait <= 0) return;
    const id = window.setTimeout(() => setResendWait((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendWait]);

  const confirmSetup = async (otp: string) => {
    if (otp.length !== 6 || inFlight.current || loading || !qr) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const { ok, data } = await postJson("/api/users/mfa/setup/confirm", mfaBody({ mfaToken, code: otp }));
      if (!ok) throw new Error(String(data.error || t("login.invalidCode")));
      const codes = Array.isArray(data.backupCodes) ? data.backupCodes.map(String) : [];
      pendingToken.current = typeof data.token === "string" ? data.token : null;
      setBackupCodes(codes);
      setStep("backup-ack");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("login.invalidCode"));
      setCode("");
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  const acknowledgeBackup = () => {
    const token = pendingToken.current;
    if (token) finishWithToken(token);
  };

  const heading =
    step === "backup-ack"
      ? t("login.backupCodesTitle")
      : step === "setup"
        ? t("login.setupAuthenticator")
        : mode === "email"
          ? t("login.emailCode")
          : t("login.authenticatorCode");
  const hint =
    step === "backup-ack"
      ? t("login.backupCodesHint")
      : step === "setup"
        ? t("login.setupAuthenticatorHint")
        : mode === "email"
          ? t("login.emailCodeHint")
          : t("login.authenticatorCodeHint");

  const trustCheckbox = (
    <label className="flex items-start gap-2.5 text-left cursor-pointer select-none">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#243c96] focus:ring-indigo-500"
        checked={trustDevice}
        onChange={(e) => setTrustDevice(e.target.checked)}
      />
      <span>
        <span className="block text-sm font-medium text-white">{t("login.trustThisDevice")}</span>
        <span className="block text-xs text-white/75 leading-snug mt-0.5">{t("login.trustThisDeviceHint")}</span>
      </span>
    </label>
  );

  if (step === "setup") {
    return (
      <div className="relative p-3 sm:p-4 lg:p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
          <div className="rv-mfa-keyboard-hide flex flex-col items-center gap-2 shrink-0 md:w-[11.75rem]">
            {qr ? (
              <div className="bg-white rounded-2xl p-2 w-fit shadow-sm">
                <img src={qr} alt="" width={148} height={148} className="block rounded-lg" />
              </div>
            ) : loading ? (
              <p className="text-center text-sm text-white/80 py-10">{t("login.preparingAuthenticator")}</p>
            ) : null}
            <button
              type="button"
              onClick={() => setShowSecret((v) => !v)}
              className="text-sm text-white/90 hover:text-white"
            >
              {t("login.cantScan")}
            </button>
            {showSecret && secret && (
              <p className="text-center font-mono text-xs tracking-widest text-white bg-black/20 rounded-xl px-2 py-1.5 select-all">
                {secret}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2.5 min-w-0 flex-1">
            <div className="rv-mfa-keyboard-hide">
              <LanguageSelector variant="login" />
            </div>
            <div className="flex flex-col items-center md:items-start text-center md:text-left gap-1.5">
              <div className="rv-mfa-keyboard-hide h-9 w-9 rounded-2xl bg-white/90 border border-white/40 flex items-center justify-center shadow-sm">
                <ShieldCheck className="w-4 h-4 text-[#243c96]" />
              </div>
              <h2 className="text-lg font-bold text-white tracking-tight">{heading}</h2>
              <p className="rv-mfa-keyboard-hide text-sm text-white/80 leading-snug">{hint}</p>
              <p className="rv-mfa-keyboard-hide text-xs text-white/70 font-medium">{email}</p>
            </div>
            {error && (
              <div className="p-2.5 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm text-center">{error}</div>
            )}
            <label className="block text-xs font-bold text-white uppercase tracking-wider text-center md:text-left">
              {t("login.confirmCode")}
            </label>
            <OtpBoxes value={code} onChange={setCode} disabled={loading || !qr} error={!!error} autoComplete="one-time-code" />
            <div className="rv-mfa-keyboard-hide">{trustCheckbox}</div>
            <button
              type="button"
              disabled={loading || code.length !== 6 || !qr}
              onClick={() => void confirmSetup(code)}
              className="w-full py-3 bg-[#243c96] text-white font-bold rounded-xl hover:bg-[#1e327f] transition-all disabled:opacity-80 disabled:cursor-not-allowed"
            >
              {loading ? t("login.verifying") : t("login.verifyAndSignIn")}
            </button>
            <button type="button" onClick={onBack} className="rv-mfa-keyboard-hide w-full text-sm text-white/90 hover:text-white flex items-center justify-center gap-1">
              <Lock className="w-3.5 h-3.5" />
              {t("login.backToSignIn")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative space-y-3 p-4 sm:space-y-5 sm:p-5 lg:p-6">
      <div className="rv-mfa-keyboard-hide">
        <LanguageSelector variant="login" />
      </div>
      <div className="flex flex-col items-center text-center gap-1.5 sm:gap-2">
        <div className="rv-mfa-keyboard-hide h-11 w-11 rounded-2xl bg-white/90 border border-white/40 flex items-center justify-center shadow-sm">
          {step === "backup-ack" ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          ) : (
            <ShieldCheck className="w-5 h-5 text-[#243c96]" />
          )}
        </div>
        <h2 className="text-lg font-bold text-white tracking-tight">{heading}</h2>
        <p className="rv-mfa-keyboard-hide text-sm text-white/80 leading-relaxed max-w-[20rem]">{hint}</p>
        <p className="rv-mfa-keyboard-hide text-xs text-white/70 font-medium">{email}</p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm text-center">{error}</div>
      )}

      {step === "code" && !useBackup && (
        <>
          <OtpBoxes
            value={code}
            onChange={setCode}
            disabled={loading}
            error={!!error}
            autoComplete="one-time-code"
            autoSubmit={(v) => void (mode === "email" ? verifyEmail(v) : verifyLogin(v))}
            onBackupCode={mode === "email" ? undefined : startBackupEntry}
          />
          <div className="rv-mfa-keyboard-hide">{trustCheckbox}</div>
          <button
            type="button"
            disabled={loading || code.length !== 6}
            onClick={() => void (mode === "email" ? verifyEmail(code) : verifyLogin(code))}
            className="w-full py-4 bg-[#243c96] text-white font-bold rounded-xl hover:bg-[#1e327f] transition-all disabled:opacity-80 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                {t("login.verifying")}
              </span>
            ) : (
              t("login.verifyAndSignIn")
            )}
          </button>
          {mode === "email" ? (
            <button
              type="button"
              disabled={loading || resendWait > 0}
              onClick={() => void resendEmail()}
              className="rv-mfa-keyboard-hide w-full text-sm text-white hover:text-white/85 disabled:opacity-70"
            >
              {resendWait > 0 ? t("login.resendIn", { seconds: resendWait }) : t("login.resendEmailCode")}
            </button>
          ) : (
            <button type="button" onClick={() => startBackupEntry()} className="rv-mfa-keyboard-hide w-full text-sm text-white hover:text-white/85">
              {t("login.useBackupCode")}
            </button>
          )}
        </>
      )}

      {step === "code" && useBackup && (
        <>
          <label className="block text-xs font-bold text-white uppercase tracking-wider">{t("login.backupCode")}</label>
          <BackupBoxes
            value={backup}
            onChange={setBackup}
            disabled={loading}
            error={!!error}
            autoSubmit={(v) => void verifyBackup(v)}
          />
          <p className="rv-mfa-keyboard-hide text-xs text-white/75 text-center">{t("login.backupCodeHint")}</p>
          <div className="rv-mfa-keyboard-hide">{trustCheckbox}</div>
          <button
            type="button"
            disabled={loading || compactAlnum(backup, 8).length !== 8}
            onClick={() => void verifyBackup()}
            className="w-full py-4 bg-[#243c96] text-white font-bold rounded-xl hover:bg-[#1e327f] transition-all disabled:opacity-80"
          >
            {loading ? t("login.verifying") : t("login.verifyAndSignIn")}
          </button>
          <button type="button" onClick={() => { setUseBackup(false); setError(null); setBackup(""); }} className="rv-mfa-keyboard-hide w-full text-sm text-white hover:text-white/85">
            {t("login.useAuthenticatorCode")}
          </button>
        </>
      )}

      {step === "backup-ack" && (
        <>
          <div className="grid grid-cols-2 gap-2">
            {backupCodes.map((c) => (
              <div key={c} className="font-mono text-sm text-slate-900 bg-white rounded-xl px-3 py-2 text-center tracking-wider">
                {c}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={acknowledgeBackup}
            className="w-full py-4 bg-[#243c96] text-white font-bold rounded-xl hover:bg-[#1e327f] transition-all"
          >
            {t("login.iveSavedTheseCodes")}
          </button>
        </>
      )}

      {step !== "backup-ack" && (
        <button type="button" onClick={onBack} className="rv-mfa-keyboard-hide w-full text-sm text-white/90 hover:text-white flex items-center justify-center gap-1">
          <Lock className="w-3.5 h-3.5" />
          {t("login.backToSignIn")}
        </button>
      )}
    </div>
  );
};
